import type { PageDataStatus } from '../../ui/page-state/page-data-state.model';

/**
 * The shared filter state's vocabulary (AITH-44, data-model §1–§6). Every
 * literal this feature puts on the wire, in the URL, or in a comparison is
 * declared here once (Art. XI-A: no hard-coded strings anywhere else).
 *
 * Nothing in this module is persisted. The URL is the only filter persistence
 * (FR-011/FR-012); `aith.ui.sidebar.collapsed` remains the sole localStorage key.
 */

// ─── §1 Date range selection ─────────────────────────────────────────────────

export type DateRangePreset = 'today' | 'last7' | 'last30' | 'last90' | 'mtd' | 'qtd' | 'ytd';

/**
 * Canonical preset order — the order the picker renders and the set the URL
 * reader validates against. FROZEN: these tokens appear in shared links
 * (filter-url contract, "Frozen surface").
 */
export const DATE_RANGE_PRESETS: readonly DateRangePreset[] = [
  'today',
  'last7',
  'last30',
  'last90',
  'mtd',
  'qtd',
  'ytd',
];

/** Human labels for the preset tokens (FR-002). */
export const DATE_RANGE_PRESET_LABELS: Readonly<Record<DateRangePreset, string>> = {
  today: 'Today',
  last7: 'Last 7 Days',
  last30: 'Last 30 Days',
  last90: 'Last 90 Days',
  mtd: 'Month to Date',
  qtd: 'Quarter to Date',
  ytd: 'Year to Date',
};

/**
 * The user's *choice*, kept separate from its resolution so a preset stays a
 * preset across UTC-day rollover — a `today` link opened tomorrow means
 * tomorrow (data-model §1, R-018).
 */
export type DateRangeSelection =
  | { readonly kind: 'preset'; readonly preset: DateRangePreset }
  | {
      readonly kind: 'custom';
      /** UTC calendar day, `YYYY-MM-DD`. */
      readonly startUtcDay: string;
      /** UTC calendar day, `YYYY-MM-DD`, inclusive. */
      readonly endUtcDay: string;
    };

// ─── §2 Resolved range ───────────────────────────────────────────────────────

/**
 * Derived, never stored, never in the URL. Half-open `[fromUtc, toUtc)` on UTC
 * midnights — the shape both window-accepting operations document (R-010).
 */
export interface ResolvedDateRange {
  /** ISO-8601 UTC midnight, inclusive. */
  readonly fromUtc: string;
  /** ISO-8601 UTC midnight, EXCLUSIVE — the next midnight after the last included day. */
  readonly toUtc: string;
}

// ─── §3 Filter state ─────────────────────────────────────────────────────────

/** The fixed three-value status set (FR-007). Closed — never widened. */
export type StatusValue = 'success' | 'error' | 'filtered';

/**
 * Canonical status order (R-006). Selections are held and serialized in this
 * order so the same selection always produces a byte-identical URL (SC-002).
 * FROZEN: both the order and the CSV separator are part of the shared link.
 */
export const STATUS_VALUES: readonly StatusValue[] = ['success', 'error', 'filtered'];

/** The seven dimensions, in bar order. Used by telemetry and the URL writer. */
export type FilterDimension =
  | 'dateRange'
  | 'client'
  | 'app'
  | 'model'
  | 'provider'
  | 'user'
  | 'status';

export const FILTER_DIMENSIONS: readonly FilterDimension[] = [
  'dateRange',
  'client',
  'app',
  'model',
  'provider',
  'user',
  'status',
];

/** The three open-set (observed-value) dimensions — app, model, provider (FR-006). */
export type OpenSetDimension = 'app' | 'model' | 'provider';

export const OPEN_SET_DIMENSIONS: readonly OpenSetDimension[] = ['app', 'model', 'provider'];

/**
 * The single shared selection set (data-model §3). One root-provided instance
 * per session (FR-009). Every field except `dateRange` is
 * nullable/empty-meaning-unset.
 */
export interface FilterState {
  readonly dateRange: DateRangeSelection;
  /** `null` = all clients. Internal roles only; identity-derived for client roles (FR-005). */
  readonly clientCode: string | null;
  readonly app: string | null;
  readonly model: string | null;
  readonly provider: string | null;
  /** Free text, exact match, commit-only. NEVER `''` — an empty commit clears to `null` (R-014). */
  /**
   * GH-164 C-4: the BLANK segment is selected, not a value. Mutually exclusive with the matching
   * value field (a setter for one clears the other), and never spelled as the text `Unspecified`,
   * because a value can genuinely carry that name. Projected as `{dim}State=unspecified`.
   */
  readonly appUnspecified: boolean;
  readonly modelUnspecified: boolean;
  readonly providerUnspecified: boolean;
  readonly user: string | null;
  /** Duplicate-free subset of {@link STATUS_VALUES}, held in canonical order. `[]` = no constraint. */
  readonly status: readonly StatusValue[];
}

export const DEFAULT_DATE_RANGE: DateRangeSelection = Object.freeze({
  kind: 'preset',
  preset: 'last30',
} as const);

/** Default state: Last 30 Days, everything else unset (FR-002, FR-014). */
export const DEFAULT_FILTER_STATE: FilterState = Object.freeze({
  dateRange: DEFAULT_DATE_RANGE,
  clientCode: null,
  app: null,
  model: null,
  provider: null,
  appUnspecified: false,
  modelUnspecified: false,
  providerUnspecified: false,
  user: null,
  status: Object.freeze([] as readonly StatusValue[]),
} as const);

// ─── §4 Published scope ──────────────────────────────────────────────────────

/**
 * What subscribed views consume (data-model §4). Deliberately carries the
 * *resolved* range so no consumer re-implements UTC arithmetic.
 *
 * Consumer note (R-016): no published API operation accepts `app`, `model`,
 * `provider`, `user`, or `status` as a filter predicate. They are carried
 * faithfully so consumers can honour them once a predicate exists; today only
 * `range` (two operations) and `clientCode` (staff drill-down) are servable.
 */
export interface FilterScope {
  readonly range: ResolvedDateRange;
  /** Already role-resolved: identity-derived for client roles, selection for internal. */
  readonly clientCode: string | null;
  readonly app: string | null;
  readonly model: string | null;
  readonly provider: string | null;
  /** GH-164 C-4: the blank segment is selected for this dimension (see {@link FilterState}). */
  readonly appUnspecified: boolean;
  readonly modelUnspecified: boolean;
  readonly providerUnspecified: boolean;
  readonly user: string | null;
  readonly status: readonly StatusValue[];
}

/** Value equality over a published scope — the guard against duplicate emissions. */
/**
 * Selection identity for the date range — deliberately NOT window equality.
 *
 * `today` and a one-day Custom range covering today resolve to the same
 * half-open window but are different selections, and data-model §1 requires
 * them to produce different URLs. Comparing the resolved window here would
 * collapse them and leave the URL describing the selection the user just left.
 */
export function dateRangeSelectionEquals(
  left: DateRangeSelection,
  right: DateRangeSelection,
): boolean {
  if (left.kind !== right.kind) return false;
  return left.kind === 'preset' && right.kind === 'preset'
    ? left.preset === right.preset
    : left.kind === 'custom' && right.kind === 'custom'
      && left.startUtcDay === right.startUtcDay
      && left.endUtcDay === right.endUtcDay;
}

/**
 * Whole-selection identity: what the URL projects (FR-011) and what a
 * publication is deduplicated on.
 *
 * {@link filterScopeEquals} answers a different question — "would a consumer
 * fetch the same data?" — and two distinct selections can share one answer.
 * Publishing on selection identity keeps the URL truthful; the consumer-facing
 * `scope` signal still dedupes on the resolved window so an equivalent
 * selection never forces a refetch.
 */
export function filterStateEquals(left: FilterState, right: FilterState): boolean {
  return (
    dateRangeSelectionEquals(left.dateRange, right.dateRange)
    && left.clientCode === right.clientCode
    && left.app === right.app
    && left.model === right.model
    && left.provider === right.provider
    && left.appUnspecified === right.appUnspecified
    && left.modelUnspecified === right.modelUnspecified
    && left.providerUnspecified === right.providerUnspecified
    && left.user === right.user
    && left.status.length === right.status.length
    && left.status.every((value, index) => value === right.status[index])
  );
}

export function filterScopeEquals(left: FilterScope, right: FilterScope): boolean {
  return (
    left.range.fromUtc === right.range.fromUtc
    && left.range.toUtc === right.range.toUtc
    && left.clientCode === right.clientCode
    && left.app === right.app
    && left.model === right.model
    && left.provider === right.provider
    && left.appUnspecified === right.appUnspecified
    && left.modelUnspecified === right.modelUnspecified
    && left.providerUnspecified === right.providerUnspecified
    && left.user === right.user
    && left.status.length === right.status.length
    && left.status.every((value, index) => value === right.status[index])
  );
}

// ─── §5 Option lists ─────────────────────────────────────────────────────────

/**
 * Genuine reuse of {@link PageDataStatus} — a type-level `Extract`, not a copied
 * token list, so renaming a token there breaks this at compile time (data-model
 * §5). `idle` is excluded: the service fetches on first need and has no
 * pre-fetch resting state.
 *
 * **Token collision — read carefully.** `success` and `error` appear in two
 * unrelated unions in this module. {@link StatusValue} is a *filter dimension
 * value the user checks*; `FilterOptionListStatus` is a *dropdown's fetch
 * state*. `status: 'error'` on a {@link FilterOptionList} means "this list could
 * not be obtained", never "the user filtered for errored calls". Never widen one
 * into the other.
 */
export type FilterOptionListStatus = Extract<
  PageDataStatus,
  'loading' | 'success' | 'empty' | 'error'
>;

export interface FilterOptionList {
  readonly status: FilterOptionListStatus;
  readonly values: readonly string[];
  /**
   * GH-164: at least one event in the probe window carried no value for the dimension, so the
   * selector may offer the blank-segment choice. Absent means false.
   */
  readonly unspecifiedObserved?: boolean;
}

/** Which option lists the bar fetches. `status` is a compile-time constant; `user` is never enumerated (spec C2). */
export type FilterOptionDimension = OpenSetDimension | 'client';

export const LOADING_OPTION_LIST: FilterOptionList = Object.freeze({
  status: 'loading',
  values: Object.freeze([] as readonly string[]),
} as const);

/** The FR-016 unavailable state: `422` row cap, network failure, or staff-with-no-client (R-003). */
export const UNAVAILABLE_OPTION_LIST: FilterOptionList = Object.freeze({
  status: 'error',
  values: Object.freeze([] as readonly string[]),
} as const);

/** Ready, but zero options — a truthful answer, deliberately distinct from unavailable (FR-016). */
export const EMPTY_OPTION_LIST: FilterOptionList = Object.freeze({
  status: 'empty',
  values: Object.freeze([] as readonly string[]),
} as const);

/**
 * `dimensionState` values on a grouped-usage row. Only `specified` rows carry a
 * selectable value; `unspecified` marks telemetry with no value for that
 * dimension (data-model §5 derivation, step 1).
 */
export const DIMENSION_STATE_SPECIFIED = 'specified';

/**
 * R-002: the option-list probe window is a fixed trailing 30 UTC days, computed
 * once per session and deliberately independent of the user's selected range —
 * binding it to the selection would refetch all three lists on every date
 * change, contradicting FR-015 outright. 30 keeps a day of headroom under the
 * API's 31-day cap.
 */
export const OPTION_PROBE_WINDOW_DAYS = 30;

// ─── §6 URL surface (FROZEN) ─────────────────────────────────────────────────

/**
 * The shareable URL's parameter names. **FROZEN** (filter-url contract):
 * changing one is a breaking change to every link already in circulation.
 */
export const FILTER_URL_PARAMS = Object.freeze({
  dateRange: 'dateRange',
  from: 'from',
  to: 'to',
  client: 'client',
  app: 'app',
  model: 'model',
  provider: 'provider',
  appState: 'appState',
  modelState: 'modelState',
  providerState: 'providerState',
  user: 'user',
  status: 'status',
} as const);

/**
 * GH-164 C-4: the only value a `{dim}State` URL parameter carries, spelled exactly as the API's
 * `{dim}State` query parameter and its responses' `dimensionState`.
 */
export const FILTER_URL_UNSPECIFIED_STATE = 'unspecified';

/** Every parameter this feature owns — the set the writer clears on reset. */
export const FILTER_URL_PARAM_NAMES: readonly string[] = Object.freeze(
  Object.values(FILTER_URL_PARAMS),
);

/** FROZEN: the `status` CSV separator (filter-url contract, "Frozen surface"). */
export const FILTER_URL_STATUS_SEPARATOR = ',';

/** The one summarising notice per load when a recognized parameter was invalid (FR-013, SC-003). */
export const INVALID_FILTER_PARAMS_MESSAGE =
  'Some filters in this link were invalid and have been reset to their defaults.';
